"use client";

// Google's own "Continue with Google" button (Google Identity Services). Calls
// onCredential with the ID token, which the server verifies.

import { useEffect, useRef, useState } from "react";
import { loadGis } from "@/lib/invoice/gdrive";
import { googleClientId, loadServerConfig } from "@/lib/invoice/account";

// The site's Google Client ID (set in the admin panel); empty until one is configured.
export function useGoogleClientId() {
  const [id, setId] = useState(googleClientId());
  useEffect(() => {
    let alive = true;
    loadServerConfig().then((c) => alive && setId(c?.googleClientId || googleClientId()));
    return () => {
      alive = false;
    };
  }, []);
  return id;
}

export default function GoogleButton({ onCredential, text = "continue_with", theme = "outline", width = 300, className = "" }) {
  const box = useRef(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;
  const [clientId, setClientId] = useState(googleClientId());
  const [err, setErr] = useState("");

  useEffect(() => {
    // always ask the server – the Client ID can change in the admin panel
    loadServerConfig().then((c) => c.googleClientId && c.googleClientId !== clientId && setClientId(c.googleClientId));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!clientId || !box.current) return;
    let alive = true;
    loadGis()
      .then(() => {
        if (!alive || !box.current) return;
        window.google.accounts.id.initialize({ client_id: clientId, callback: (r) => cb.current?.(r.credential), ux_mode: "popup", auto_select: false, cancel_on_tap_outside: true });
        box.current.innerHTML = "";
        // never wider than the space it sits in (Google's button has a fixed pixel width, 200–400)
        const room = box.current.parentElement?.clientWidth || width;
        window.google.accounts.id.renderButton(box.current, { type: "standard", theme, size: "large", text, shape: "pill", logo_alignment: "left", width: Math.max(200, Math.min(width, room, 400)) });
      })
      .catch((e) => setErr(e.message));
    return () => {
      alive = false;
    };
  }, [clientId, text, theme, width]);

  if (!clientId) return null;
  return (
    <div className={className}>
      <div ref={box} className="flex min-h-[44px] justify-center" data-testid="google-button" />
      {err && <div className="mt-1 text-center text-xs text-red-600">{err}</div>}
    </div>
  );
}
